var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import React, { useState, useEffect, useMemo } from 'react';
import { FlightSelect } from '@flybits/design-system';
import { COMPARE_WITH, BOOL_STATEMENTS, BOOL_VALUES } from '../constants/dropdowns';
import { EXPRESSION_PART as EP } from '../constants/types';
import RuleFrame from './shared/RuleFrame';
import ParametersFrame from './shared/ParametersFrame';
import { mapBlockToRuleFrameProps } from './shared/helpers';
import { getConstraints } from '../utils/helpers';
import HistoricalQueries from './shared/HistoricalQueries';
import PluginDropdown from './shared/PluginDropdown';
var BooleanLiteral = function (renderProps) {
    var _a;
    var logicString = renderProps.logicString, uiBlock = renderProps.uiBlock, updateUiBlock = renderProps.updateUiBlock;
    var _b = getConstraints(uiBlock), isValueLocked = _b.isValueLocked, operationConstraint = _b.operationConstraint;
    var isOperatorLocked = operationConstraint === 'locked' || operationConstraint === 'historical';
    var STATEMENTS = useMemo(function () { return (operationConstraint === 'regular' ? BOOL_STATEMENTS.filter(function (s) { return s.type !== 'historical'; }) : BOOL_STATEMENTS); }, [operationConstraint]);
    var DEFAULT_CLASS = 'rule-children';
    var OPTION_FIELD_WIDTH = '150px';
    var getBlockOperator = function (uiBlock) {
        var _a;
        if (uiBlock.type !== 'historical') {
            return (_a = BOOL_STATEMENTS.find(function (option) { return option.value === uiBlock.operator && option.type === uiBlock.type; })) === null || _a === void 0 ? void 0 : _a.exp;
        }
        else {
            return uiBlock.operator;
        }
    };
    var _c = __read(useState(function () {
        var _a, _b;
        if (uiBlock.type === 'historical') {
            return ((_a = BOOL_STATEMENTS.find(function (option) {
                return option.type === uiBlock.type &&
                    option.exp === getBlockOperator(uiBlock) &&
                    option.negate === uiBlock.historicalValue.negate;
            })) !== null && _a !== void 0 ? _a : BOOL_STATEMENTS[0]);
        }
        else {
            return ((_b = BOOL_STATEMENTS.find(function (option) { return option.type === uiBlock.type && option.exp === getBlockOperator(uiBlock); })) !== null && _b !== void 0 ? _b : BOOL_STATEMENTS[0]);
        }
    }), 2), selectedStatement = _c[0], setSelectedStatement = _c[1];
    var _d = __read(useState(COMPARE_WITH[0]), 2), selectedComparision = _d[0], setSelectedComparision = _d[1];
    var _e = __read(useState((_a = BOOL_VALUES.find(function (option) { return option.key === uiBlock.value; })) !== null && _a !== void 0 ? _a : BOOL_VALUES[0]), 2), selectedValue = _e[0], setSelectedValue = _e[1];
    var _f = __read(useState(false), 2), isHistorical = _f[0], setIsHistorical = _f[1];
    var handleDropdownChange = function (option, type) {
        var _a, _b;
        switch (type) {
            case EP.OPERATOR:
                setSelectedStatement(option);
                var histValue = __assign(__assign({}, uiBlock.historicalValue), { negate: (_a = option.negate) !== null && _a !== void 0 ? _a : false }) || {
                    negate: (_b = option.negate) !== null && _b !== void 0 ? _b : false,
                };
                histValue.histOperator = histValue.histOperator ? histValue.histOperator : 'lseq';
                histValue.durationUnit = histValue.durationUnit ? histValue.durationUnit : 'minute(s)';
                var durationUnitValue = uiBlock.durationUnit ? uiBlock.durationUnit : 'minute(s)';
                var histOperatorValue = uiBlock.histOperator ? uiBlock.histOperator : 'lseq';
                updateUiBlock(__assign(__assign({}, uiBlock), { operator: option.type === 'historical' ? option.exp : option.value, type: option.type, histOperator: histOperatorValue, historicalValue: histValue, durationUnit: durationUnitValue }));
                break;
            // this legit does nothing rn
            case EP.COMPAREWITH:
                setSelectedComparision(option);
                break;
            case EP.CONST:
                setSelectedValue(option);
                updateUiBlock(__assign(__assign({}, uiBlock), { value: option.key }));
                break;
        }
    };
    // Context Plugin
    var _g = __read(useState(false), 2), isPlugin = _g[0], setIsPlugin = _g[1];
    useEffect(function () {
        setIsPlugin(!!uiBlock.contextComparisonPlugin);
    }, [uiBlock.contextComparisonPlugin]);
    useEffect(function () {
        if (selectedStatement.type === 'historical') {
            setIsHistorical(true);
        }
        else {
            setIsHistorical(false);
        }
    }, [selectedStatement]);
    useEffect(function () {
        // set defaults on initialization
        // we can preset this if repainting is an issue
        if (uiBlock.value === '' && uiBlock.operator === '') {
            updateUiBlock(__assign(__assign({}, uiBlock), { value: selectedValue.key, operator: selectedStatement.value }));
        }
    }, []);
    return (React.createElement(RuleFrame, { renderProps: mapBlockToRuleFrameProps(renderProps) },
        React.createElement("div", { className: DEFAULT_CLASS },
            React.createElement(FlightSelect, { className: "".concat(DEFAULT_CLASS, "__item"), options: STATEMENTS, selected: selectedStatement, handleOptionClick: function (option) { return handleDropdownChange(option, EP.OPERATOR); }, disabled: isOperatorLocked, width: OPTION_FIELD_WIDTH }),
            isPlugin ? (React.createElement(PluginDropdown, __assign({}, renderProps))) : (React.createElement(React.Fragment, null,
                React.createElement(FlightSelect, { className: "".concat(DEFAULT_CLASS, "__item"), options: BOOL_VALUES, selected: selectedValue, disabled: isOperatorLocked || (isValueLocked && !isHistorical), handleOptionClick: function (option) { return handleDropdownChange(option, EP.CONST); }, width: OPTION_FIELD_WIDTH }))),
            isHistorical && (React.createElement(React.Fragment, null,
                React.createElement("br", null),
                React.createElement(HistoricalQueries, __assign({}, renderProps))))),
        React.createElement(ParametersFrame, __assign({}, renderProps))));
};
export default BooleanLiteral;
//# sourceMappingURL=BooleanLiteral.js.map